import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * IP allow/block lists, live evaluation and automated mitigation (part 33).
 *
 * The page is the operator's console for the same rules the API evaluates: create lists, add
 * CIDRs with a reason and an optional expiry, test one address without touching the lists, turn
 * a failed-login batch into time-boxed blocks, and copy the exact Cloudflare custom-rule payload.
 * An allow entry always beats a block entry; the evaluator names the block it overrode instead
 * of hiding that behind a green badge.
 */

type IPKind = 'allow' | 'block'

interface IPListRow {
  id: string
  name: string
  description: string
  kind: IPKind
  kind_label: string
  enabled: boolean
  entry_count: number
  active_entry_count: number
  created_at: string
  updated_at: string
}

interface IPEntryRow {
  id: string
  list_id: string
  list_name?: string
  list_kind?: IPKind
  cidr: string
  note: string
  reason_code: string
  reason: string
  source: string
  expires_at?: string
  expired: boolean
  created_at: string
}

interface IPDecision {
  ip: string
  blocked: boolean
  allowlisted: boolean
  reason_code: string
  reason: string
  matched_entry_id?: string
  matched_list_name?: string
  matched_cidr?: string
  matched_expires_at?: string
  overridden_block_entry_id?: string
  overridden_block_list_name?: string
  overridden_block_cidr?: string
  overridden_block_reason_code?: string
  allow_matches: number
  block_matches: number
  expired_entries: number
  evaluated_at: string
}

interface IPBlockRow {
  id: string
  list_name?: string
  cidr: string
  reason: string
  source: string
  failed_attempts: number
  window_started_at: string
  window_ended_at: string
  blocked_at: string
  expires_at?: string
}

interface IPEscalationRow {
  ip: string
  cidr: string
  action: 'block' | 'skip'
  reason_code: string
  reason: string
  failures: number
  window_start: string
  window_end: string
  expires_at?: string
}

interface IPEscalation {
  report: {
    policy: { threshold: number; window_minutes: number; block_minutes: number }
    generated_at: string
    failures: number
    distinct_ips: number
    candidates: number
    blocked: number
    skipped: number
    results: IPEscalationRow[]
  }
  entries: IPEntryRow[]
  blocks: IPBlockRow[]
}

interface WAFRule {
  action: string
  description: string
  enabled: boolean
  expression: string
  ref: string
}

interface WAFExport {
  zone_id?: string
  phase: string
  endpoint: string
  action: string
  payload: { rules: WAFRule[] }
  block_cidrs: string[]
  allow_cidrs: string[]
  expired_ignored: number
  generated_at: string
  notes: string[]
}

interface ReasonOption {
  code: string
  label: string
}

interface IPMeta {
  kinds: Array<{ value: IPKind; label: string }>
  reasons: Record<IPKind, ReasonOption[]>
  sources: Array<{ code: string; label: string }>
  waf_actions: string[]
  waf_phase: string
  default_escalation: { threshold: number; window_minutes: number; block_minutes: number }
  max_note_length: number
  max_failures_per_run: number
}

const LIST_KEY = ['ip-lists']
const BLOCK_KEY = ['ip-blocks']
const META_KEY = ['ip-list-meta']
const ENTRY_KEY = ['ip-list-entries']

const wafActionLabels: Record<string, string> = {
  block: 'บล็อก',
  managed_challenge: 'Managed Challenge',
  js_challenge: 'JS Challenge',
  challenge: 'Interactive Challenge',
  log: 'บันทึกเท่านั้น',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function kindTone(kind: IPKind): string {
  return kind === 'allow' ? 'sev-low' : 'sev-critical'
}

export function IPLists() {
  const { me } = useAuth()
  const role = me?.role ?? 'auditor'
  const canView = role === 'owner' || role === 'admin' || role === 'audit_manager' || role === 'auditor'
  const canConfigure = role === 'owner' || role === 'admin'
  const canEscalate = canConfigure || role === 'audit_manager'
  const qc = useQueryClient()

  const [notice, setNotice] = useState('')
  const [formError, setFormError] = useState('')

  // Create-list form.
  const [listName, setListName] = useState('')
  const [listKind, setListKind] = useState<IPKind>('block')
  const [listDescription, setListDescription] = useState('')

  // Selected list and entry form.
  const [selectedListID, setSelectedListID] = useState('')
  const [entryCIDR, setEntryCIDR] = useState('')
  const [entryNote, setEntryNote] = useState('')
  const [entryReason, setEntryReason] = useState('')
  const [entryExpires, setEntryExpires] = useState('')

  // Evaluator.
  const [evalIP, setEvalIP] = useState('')
  const [decision, setDecision] = useState<IPDecision | null>(null)

  // Escalation.
  const [escalateListID, setEscalateListID] = useState('')
  const [failureText, setFailureText] = useState('')
  const [threshold, setThreshold] = useState('5')
  const [windowMinutes, setWindowMinutes] = useState('10')
  const [blockMinutes, setBlockMinutes] = useState('1440')
  const [escalation, setEscalation] = useState<IPEscalation | null>(null)

  // WAF export.
  const [zoneID, setZoneID] = useState('')
  const [wafAction, setWafAction] = useState('block')
  const [waf, setWaf] = useState<WAFExport | null>(null)

  const meta = useQuery({
    queryKey: META_KEY,
    queryFn: () => api<IPMeta>('/v1/ip-lists/meta'),
    enabled: canView,
    staleTime: 300_000,
  })

  const lists = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => list<IPListRow>('/v1/ip-lists'),
    enabled: canView,
    staleTime: 15_000,
  })

  const entries = useQuery({
    queryKey: [...ENTRY_KEY, selectedListID],
    queryFn: () => list<IPEntryRow>(`/v1/ip-lists/entries?list_id=${encodeURIComponent(selectedListID)}`),
    enabled: canView && selectedListID !== '',
    staleTime: 15_000,
  })

  const blocks = useQuery({
    queryKey: BLOCK_KEY,
    queryFn: () => list<IPBlockRow>('/v1/ip-lists/blocks?limit=100'),
    enabled: canView,
    staleTime: 15_000,
  })

  const listRows = lists.data ?? []
  const selectedList = useMemo(
    () => listRows.find((row) => row.id === selectedListID),
    [listRows, selectedListID],
  )
  const blockLists = useMemo(() => listRows.filter((row) => row.kind === 'block' && row.enabled), [listRows])
  const reasonOptions = selectedList ? meta.data?.reasons[selectedList.kind] ?? [] : []

  // Keep a valid selection as lists load, and keep the reason code valid for the list kind.
  useEffect(() => {
    if (listRows.length === 0) {
      if (selectedListID !== '') setSelectedListID('')
      return
    }
    if (!listRows.some((row) => row.id === selectedListID)) {
      setSelectedListID(listRows[0].id)
    }
  }, [listRows, selectedListID])

  useEffect(() => {
    if (reasonOptions.length === 0) {
      setEntryReason('')
      return
    }
    if (!reasonOptions.some((option) => option.code === entryReason)) {
      setEntryReason(reasonOptions[0].code)
    }
  }, [reasonOptions, entryReason])

  useEffect(() => {
    if (blockLists.length === 0) {
      if (escalateListID !== '') setEscalateListID('')
      return
    }
    if (!blockLists.some((row) => row.id === escalateListID)) {
      setEscalateListID(blockLists[0].id)
    }
  }, [blockLists, escalateListID])

  const createList = useMutation({
    mutationFn: () => apiWrite<IPListRow>('/v1/ip-lists', 'POST', {
      name: listName.trim(),
      description: listDescription.trim(),
      kind: listKind,
      enabled: true,
    }),
    onSuccess: (row) => {
      setListName('')
      setListDescription('')
      setSelectedListID(row.id)
      setNotice(`สร้างบัญชี “${row.name}” แล้ว`)
      setFormError('')
      void qc.invalidateQueries({ queryKey: LIST_KEY })
    },
    onError: (err) => setFormError(errorText(err, 'สร้างบัญชีไม่สำเร็จ')),
  })

  const addEntry = useMutation({
    mutationFn: () => apiWrite<IPEntryRow>('/v1/ip-lists/entries', 'POST', {
      list_id: selectedListID,
      cidr: entryCIDR.trim(),
      note: entryNote.trim(),
      reason_code: entryReason,
      source: 'manual',
      expires_at: entryExpires ? new Date(entryExpires).toISOString() : '',
    }),
    onSuccess: (row) => {
      setEntryCIDR('')
      setEntryNote('')
      setEntryExpires('')
      setNotice(`เพิ่ม ${row.cidr} ในบัญชี “${row.list_name ?? selectedList?.name ?? ''}” แล้ว`)
      setFormError('')
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: [...ENTRY_KEY, selectedListID] })
    },
    onError: (err) => setFormError(errorText(err, 'เพิ่มรายการไม่สำเร็จ')),
  })

  const evaluate = useMutation({
    mutationFn: () => apiWrite<IPDecision>('/v1/ip-lists/evaluate', 'POST', { ip: evalIP.trim() }),
    onSuccess: (row) => {
      setDecision(row)
      setFormError('')
    },
    onError: (err) => {
      setDecision(null)
      setFormError(errorText(err, 'ประเมินไอพีไม่สำเร็จ'))
    },
  })

  const escalate = useMutation({
    mutationFn: () => {
      const failures = failureText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [ip, occurred] = line.split(/[,\s]+/)
          if (!ip || !occurred) throw new Error('แต่ละบรรทัดต้องเป็น ip,เวลา RFC3339')
          return { ip, occurred_at: occurred }
        })
      return apiWrite<IPEscalation>('/v1/ip-lists/escalate', 'POST', {
        list_id: escalateListID,
        failures,
        threshold: Number.parseInt(threshold, 10),
        window_minutes: Number.parseInt(windowMinutes, 10),
        block_minutes: Number.parseInt(blockMinutes, 10),
      })
    },
    onSuccess: (result) => {
      setEscalation(result)
      setNotice(
        result.report.blocked > 0
          ? `บล็อกอัตโนมัติ ${result.report.blocked} ไอพี`
          : 'ไม่มีไอพีที่ต้องบล็อกเพิ่มเติม',
      )
      setFormError('')
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: BLOCK_KEY })
      if (selectedListID) void qc.invalidateQueries({ queryKey: [...ENTRY_KEY, selectedListID] })
    },
    onError: (err) => setFormError(errorText(err, 'ยกระดับอัตโนมัติไม่สำเร็จ')),
  })

  const exportWAF = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams({ action: wafAction })
      if (zoneID.trim()) params.set('zone_id', zoneID.trim())
      return api<WAFExport>(`/v1/ip-lists/waf?${params.toString()}`)
    },
    onSuccess: (result) => {
      setWaf(result)
      setFormError('')
    },
    onError: (err) => {
      setWaf(null)
      setFormError(errorText(err, 'สร้างเพย์โหลด WAF ไม่สำเร็จ'))
    },
  })

  async function copyPayload() {
    if (!waf) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(waf.payload, null, 2))
      setNotice('คัดลอกเพย์โหลด WAF แล้ว')
    } catch {
      setFormError('เบราว์เซอร์ไม่อนุญาตให้คัดลอก กรุณาเลือกข้อความในกล่องเพย์โหลด')
    }
  }

  const activeBlocks = listRows
    .filter((row) => row.kind === 'block')
    .reduce((sum, row) => sum + row.active_entry_count, 0)
  const activeAllows = listRows
    .filter((row) => row.kind === 'allow')
    .reduce((sum, row) => sum + row.active_entry_count, 0)

  if (!canView) {
    return (
      <>
        <PageHead title="บัญชีไอพีและมาตรการอัตโนมัติ" />
        <div className="card card-pad form-error">บทบาทของคุณไม่มีสิทธิ์ดูบัญชีไอพี</div>
      </>
    )
  }

  return (
    <>
      <PageHead
        title="บัญชีไอพีและมาตรการอัตโนมัติ"
        sub="อนุญาตและบล็อกตามเครือข่าย พร้อมเพย์โหลด Cloudflare ที่ตรงกับรายการจริง — บัญชีอนุญาตมีผลเหนือบัญชีบล็อกเสมอ"
        actions={<span className="chip">ไอพีที่บล็อกอยู่ {number(activeBlocks)} เครือข่าย</span>}
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}
      {formError ? <div className="form-error" style={{ marginBottom: 16 }}>{formError}</div> : null}

      <div className="grid-4 stagger">
        <Stat label="บัญชีที่ใช้งาน" value={number(listRows.length)} unit="บัญชี" foot="ทั้งบัญชีอนุญาตและบัญชีบล็อก" />
        <Stat label="เครือข่ายที่อนุญาต" value={number(activeAllows)} unit="รายการ" foot="ยังไม่หมดอายุ" />
        <Stat label="เครือข่ายที่บล็อก" value={number(activeBlocks)} unit="รายการ" foot="ยังไม่หมดอายุ" />
        <Stat label="บล็อกอัตโนมัติล่าสุด" value={number((blocks.data ?? []).length)} unit="รายการ" foot="เรียงตามเวลาที่บล็อก" />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">บัญชีไอพี</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>อนุญาตก่อน บล็อกทีหลัง</span>
            </div>

            {lists.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={56} /><Skel h={56} /></div>
            ) : lists.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(lists.error, 'โหลดบัญชีไอพีไม่สำเร็จ')}
              </div>
            ) : listRows.length === 0 ? (
              <Empty title="ยังไม่มีบัญชีไอพี" body="สร้างบัญชีบล็อกหรือบัญชีอนุญาตด้านล่าง แล้วเพิ่มเครือข่ายที่ต้องการ" />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>ชื่อบัญชี</th>
                      <th>ประเภท</th>
                      <th style={{ textAlign: 'right' }}>ใช้งาน / ทั้งหมด</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {listRows.map((row) => (
                      <tr key={row.id} style={row.id === selectedListID ? { background: 'rgba(255,255,255,0.02)' } : undefined}>
                        <td>
                          <div>{row.name}</div>
                          <div className="dim" style={{ fontSize: 11 }}>{row.description || 'ไม่มีคำอธิบาย'}</div>
                        </td>
                        <td>
                          <span className={`sev ${kindTone(row.kind)}`}>
                            <i className="sev-dot" />
                            {row.kind_label}
                          </span>
                          {!row.enabled ? <div className="dim" style={{ fontSize: 10.5 }}>ปิดใช้งาน</div> : null}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>
                          {number(row.active_entry_count)} / {number(row.entry_count)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm" onClick={() => setSelectedListID(row.id)}>
                            จัดการรายการ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canConfigure ? (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-line)' }}>
                <div className="card-label" style={{ marginBottom: 10 }}>สร้างบัญชีใหม่</div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <input
                    className="input"
                    style={{ flex: '1 1 180px' }}
                    placeholder="ชื่อบัญชี"
                    value={listName}
                    maxLength={120}
                    onChange={(e) => setListName(e.target.value)}
                  />
                  <select
                    className="select"
                    aria-label="ประเภทบัญชี"
                    value={listKind}
                    onChange={(e) => setListKind(e.target.value as IPKind)}
                  >
                    <option value="block">บัญชีบล็อก</option>
                    <option value="allow">บัญชีอนุญาต</option>
                  </select>
                  <input
                    className="input"
                    style={{ flex: '1 1 220px' }}
                    placeholder="คำอธิบาย (ไม่บังคับ)"
                    value={listDescription}
                    maxLength={500}
                    onChange={(e) => setListDescription(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={createList.isPending || listName.trim() === ''}
                    onClick={() => createList.mutate()}
                  >
                    {createList.isPending ? 'กำลังสร้าง…' : 'สร้างบัญชี'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">
                รายการในบัญชี{selectedList ? `: ${selectedList.name}` : ''}
              </span>
              {selectedList ? (
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {selectedList.kind_label} · ใช้งาน {number(selectedList.active_entry_count)} จาก {number(selectedList.entry_count)}
                </span>
              ) : null}
            </div>

            {selectedListID === '' ? (
              <Empty title="ยังไม่ได้เลือกบัญชี" body="เลือกบัญชีจากตารางด้านบนเพื่อดูและเพิ่มเครือข่าย" />
            ) : entries.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={48} /><Skel h={48} /></div>
            ) : entries.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(entries.error, 'โหลดรายการไม่สำเร็จ')}
              </div>
            ) : (entries.data ?? []).length === 0 ? (
              <Empty title="บัญชีนี้ยังว่าง" body="เพิ่มเครือข่าย CIDR ด้านล่าง พร้อมเหตุผลและวันหมดอายุถ้าต้องการให้มีผลชั่วคราว" />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>เครือข่าย</th>
                      <th>เหตุผล</th>
                      <th>ที่มา</th>
                      <th>หมดอายุ</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(entries.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td className="mono" style={{ fontSize: 12 }}>{row.cidr}</td>
                        <td>
                          <div>{row.reason}</div>
                          {row.note ? <div className="dim" style={{ fontSize: 11 }}>{row.note}</div> : null}
                        </td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{row.source}</td>
                        <td className="dim" style={{ fontSize: 11.5 }}>
                          {row.expires_at ? thaiDateTime(row.expires_at) : 'ถาวร'}
                        </td>
                        <td>
                          <span className={`sev ${row.expired ? 'sev-medium' : 'sev-low'}`}>
                            <i className="sev-dot" />
                            {row.expired ? 'หมดอายุ' : 'ใช้งาน'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canConfigure && selectedList ? (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-line)' }}>
                <div className="card-label" style={{ marginBottom: 10 }}>เพิ่มเครือข่าย</div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <input
                    className="input mono"
                    style={{ flex: '1 1 190px' }}
                    placeholder="203.0.113.0/24"
                    value={entryCIDR}
                    onChange={(e) => setEntryCIDR(e.target.value)}
                  />
                  <select
                    className="select"
                    aria-label="เหตุผล"
                    value={entryReason}
                    onChange={(e) => setEntryReason(e.target.value)}
                  >
                    {reasonOptions.map((option) => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                  <input
                    className="input"
                    style={{ flex: '1 1 200px' }}
                    placeholder="หมายเหตุ (ไม่บังคับ)"
                    value={entryNote}
                    maxLength={500}
                    onChange={(e) => setEntryNote(e.target.value)}
                  />
                  <input
                    className="input"
                    type="datetime-local"
                    aria-label="วันหมดอายุ"
                    value={entryExpires}
                    onChange={(e) => setEntryExpires(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={addEntry.isPending || entryCIDR.trim() === '' || entryReason === ''}
                    onClick={() => addEntry.mutate()}
                  >
                    {addEntry.isPending ? 'กำลังเพิ่ม…' : 'เพิ่มรายการ'}
                  </button>
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                  เว้นวันหมดอายุไว้หากต้องการให้มีผลถาวร ระบบจะแปลงเป็นเวลา UTC ก่อนบันทึก
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">ตรวจสอบไอพี</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              ประเมินกับบัญชีที่เปิดใช้งานทั้งหมด ไม่เขียนข้อมูลใด ๆ
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <input
                className="input mono"
                style={{ flex: 1 }}
                placeholder="198.51.100.7"
                value={evalIP}
                onChange={(e) => setEvalIP(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && evalIP.trim() !== '') evaluate.mutate()
                }}
              />
              <button
                className="btn btn-primary"
                disabled={evaluate.isPending || evalIP.trim() === ''}
                onClick={() => evaluate.mutate()}
              >
                {evaluate.isPending ? 'กำลังประเมิน…' : 'ประเมิน'}
              </button>
            </div>

            {decision ? (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-line)' }}>
                <div className="row">
                  <span className={`sev ${decision.blocked ? 'sev-critical' : 'sev-low'}`}>
                    <i className="sev-dot" />
                    {decision.blocked ? 'ถูกบล็อก' : 'ผ่านได้'}
                  </span>
                  <span className="mono dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{decision.ip}</span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 8 }}>{decision.reason}</div>
                {decision.matched_list_name ? (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                    บัญชีที่ตัดสิน: {decision.matched_list_name} · {decision.matched_cidr}
                    {decision.matched_expires_at ? ` · ถึง ${thaiDateTime(decision.matched_expires_at)}` : ''}
                  </div>
                ) : null}
                {decision.overridden_block_cidr ? (
                  <div style={{ fontSize: 11.5, marginTop: 6, color: 'var(--color-emerald-soft)' }}>
                    ผ่านเพราะอยู่ในบัญชีอนุญาต แม้ตรงกับบล็อก {decision.overridden_block_cidr}
                    {decision.overridden_block_list_name ? ` (${decision.overridden_block_list_name})` : ''}
                  </div>
                ) : null}
                <div className="dim tnum" style={{ fontSize: 10.5, marginTop: 8 }}>
                  ตรงกับอนุญาต {decision.allow_matches} · ตรงกับบล็อก {decision.block_matches} · ข้ามรายการหมดอายุ {decision.expired_entries}
                </div>
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="card-label">ยกระดับจากเหตุการณ์เข้าสู่ระบบล้มเหลว</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              วางบรรทัดละหนึ่งเหตุการณ์ในรูป ip,เวลา RFC3339 ระบบจะบล็อกเมื่อพบจำนวนครั้งถึงเกณฑ์ภายในหน้าต่างที่กำหนด
            </div>

            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <select
                className="select"
                aria-label="บัญชีบล็อก"
                value={escalateListID}
                onChange={(e) => setEscalateListID(e.target.value)}
              >
                {blockLists.length === 0 ? <option value="">ไม่มีบัญชีบล็อกที่เปิดใช้งาน</option> : null}
                {blockLists.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </select>
              <input
                className="input"
                style={{ width: 86 }}
                aria-label="จำนวนครั้ง"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
              <input
                className="input"
                style={{ width: 92 }}
                aria-label="หน้าต่าง (นาที)"
                value={windowMinutes}
                onChange={(e) => setWindowMinutes(e.target.value)}
              />
              <input
                className="input"
                style={{ width: 92 }}
                aria-label="ระยะเวลาบล็อก (นาที)"
                value={blockMinutes}
                onChange={(e) => setBlockMinutes(e.target.value)}
              />
            </div>
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
              ลำดับ: จำนวนครั้ง · หน้าต่างเป็นนาที · ระยะเวลาบล็อกเป็นนาที
            </div>

            <textarea
              className="input mono"
              style={{ width: '100%', minHeight: 104, marginTop: 10, resize: 'vertical' }}
              placeholder={'198.51.100.7,2026-11-04T11:58:00Z\n198.51.100.7,2026-11-04T11:59:00Z'}
              value={failureText}
              onChange={(e) => setFailureText(e.target.value)}
            />

            <button
              className="btn btn-primary"
              style={{ marginTop: 10 }}
              disabled={!canEscalate || escalate.isPending || escalateListID === '' || failureText.trim() === ''}
              onClick={() => escalate.mutate()}
            >
              {escalate.isPending ? 'กำลังยกระดับ…' : 'ยกระดับและบล็อก'}
            </button>

            {escalation ? (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-line)' }}>
                <div className="tnum" style={{ fontSize: 12 }}>
                  พิจารณา {number(escalation.report.distinct_ips)} ไอพี · ถึงเกณฑ์ {number(escalation.report.candidates)} ·
                  บล็อก {number(escalation.report.blocked)} · ข้าม {number(escalation.report.skipped)}
                </div>
                {escalation.report.results.length === 0 ? (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>ไม่มีไอพีใดถึงเกณฑ์ในชุดเหตุการณ์นี้</div>
                ) : (
                  <div className="table-wrap" style={{ marginTop: 10 }}>
                    <table className="table data">
                      <thead>
                        <tr>
                          <th>ไอพี</th>
                          <th>ผล</th>
                          <th style={{ textAlign: 'right' }}>ครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        {escalation.report.results.map((row) => (
                          <tr key={row.ip}>
                            <td className="mono" style={{ fontSize: 11.5 }}>{row.cidr}</td>
                            <td>
                              <span className={`sev ${row.action === 'block' ? 'sev-critical' : 'sev-medium'}`}>
                                <i className="sev-dot" />
                                {row.action === 'block' ? 'บล็อก' : 'ข้าม'}
                              </span>
                              <div className="dim" style={{ fontSize: 10.5 }}>{row.reason}</div>
                            </td>
                            <td className="tnum" style={{ textAlign: 'right' }}>{row.failures}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="card-label">ส่งออกกฎ WAF สำหรับ Cloudflare</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              สร้างเพย์โหลดจากรายการที่ยังไม่หมดอายุ บัญชีอนุญาตถูกใส่เป็นเงื่อนไขยกเว้นในทุกกฎ
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <input
                className="input mono"
                style={{ flex: '1 1 200px' }}
                placeholder="Zone ID (ไม่บังคับ)"
                value={zoneID}
                onChange={(e) => setZoneID(e.target.value)}
              />
              <select
                className="select"
                aria-label="การกระทำของ WAF"
                value={wafAction}
                onChange={(e) => setWafAction(e.target.value)}
              >
                {(meta.data?.waf_actions ?? ['block']).map((action) => (
                  <option key={action} value={action}>{wafActionLabels[action] ?? action}</option>
                ))}
              </select>
              <button
                className="btn"
                disabled={exportWAF.isPending}
                onClick={() => exportWAF.mutate()}
              >
                {exportWAF.isPending ? 'กำลังสร้าง…' : 'สร้างเพย์โหลด'}
              </button>
            </div>

            {waf ? (
              <div style={{ marginTop: 14 }}>
                <div className="dim" style={{ fontSize: 11 }}>
                  {waf.endpoint} · เฟส {waf.phase} · {number(waf.payload.rules.length)} กฎ ·
                  บล็อก {number(waf.block_cidrs.length)} เครือข่าย · อนุญาต {number(waf.allow_cidrs.length)} เครือข่าย
                </div>
                {waf.notes.map((note) => (
                  <div key={note} className="dim" style={{ fontSize: 11, marginTop: 4 }}>{note}</div>
                ))}
                <pre
                  className="mono"
                  style={{
                    marginTop: 10, padding: 12, maxHeight: 280, overflow: 'auto',
                    fontSize: 10.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    background: 'rgba(0,0,0,0.28)', border: '1px solid var(--color-line)', borderRadius: 6,
                  }}
                >
                  {JSON.stringify(waf.payload, null, 2)}
                </pre>
                <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => void copyPayload()}>
                  คัดลอกเพย์โหลด
                </button>
              </div>
            ) : null}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ประวัติการบล็อกอัตโนมัติ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>ใหม่ที่สุดก่อน</span>
            </div>
            {blocks.isLoading ? (
              <div className="stack" style={{ marginTop: 12 }}><Skel h={44} /><Skel h={44} /></div>
            ) : blocks.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(blocks.error, 'โหลดประวัติการบล็อกไม่สำเร็จ')}
              </div>
            ) : (blocks.data ?? []).length === 0 ? (
              <Empty title="ยังไม่มีการบล็อกอัตโนมัติ" body="เมื่อมีการยกระดับจากเหตุการณ์เข้าสู่ระบบล้มเหลว รายการจะปรากฏที่นี่พร้อมหลักฐานจำนวนครั้งและหน้าต่างเวลา" />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>เครือข่าย</th>
                      <th style={{ textAlign: 'right' }}>ครั้ง</th>
                      <th>หน้าต่าง</th>
                      <th>บล็อกเมื่อ</th>
                      <th>หมดอายุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(blocks.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td className="mono" style={{ fontSize: 11.5 }}>{row.cidr}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{number(row.failed_attempts)}</td>
                        <td className="dim" style={{ fontSize: 10.5 }}>
                          {thaiDateTime(row.window_started_at)} ถึง {thaiDateTime(row.window_ended_at)}
                        </td>
                        <td className="dim" style={{ fontSize: 10.5 }}>{thaiDateTime(row.blocked_at)}</td>
                        <td className="dim" style={{ fontSize: 10.5 }}>
                          {row.expires_at ? thaiDateTime(row.expires_at) : 'ถาวร'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
